import { useCallback, useEffect, useState } from 'react';
import simulationService from '../services/simulationService';
import { useTranslation } from 'react-i18next';

/**
 * SimulationTimeline — "build console" view of a running/completed simulation.
 *
 * Mirrors the UX of CI tools (Codemagic / GitHub Actions): a vertical list of
 * named steps with a status icon, a per-step duration, and an expandable area
 * for the currently-active step that shows live metrics (or, for a failed
 * step, the categorized error message).
 *
 * Polls /api/simulations/{id}/timeline/ every 10s while overall_status is
 * queued or running. Stops polling once the simulation is in a terminal
 * state (completed / failed) to avoid hammering the backend.
 *
 * The backend never exposes internal solver / infra terminology, so every
 * label rendered here is safe to show to end-users as-is.
 */
const STATUS_ICONS = {
  done: { symbol: '✓', cls: 'bg-green-500/20 text-green-400 border-green-500/40' },
  running: { symbol: '⋯', cls: 'bg-primary-500/20 text-primary-400 border-primary-500/40 animate-pulse' },
  pending: { symbol: '○', cls: 'bg-dark-700 text-dark-400 border-dark-600' },
  failed: { symbol: '×', cls: 'bg-red-500/20 text-red-400 border-red-500/40' },
};

function formatDuration(seconds) {
  if (seconds == null || seconds < 0) return null;
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return s > 0 ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm > 0 ? `${h}h ${mm}m` : `${h}h`;
}

function relativeTime(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  const diff = Math.floor((Date.now() - then) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
  return `${Math.floor(diff / 86400)} d ago`;
}

function StepRow({ step, isCurrent, isExpanded, onToggle, error }) {
  const icon = STATUS_ICONS[step.status] || STATUS_ICONS.pending;
  const duration = formatDuration(step.duration_seconds);
  const hasDetails = isCurrent || step.status === 'failed' || (error && error.failed_step_id === step.id);
  const isFailed = step.status === 'failed' || (error && error.failed_step_id === step.id);

  return (
    <div className="border-b border-dark-700 last:border-b-0">
      <button
        type="button"
        onClick={hasDetails ? onToggle : undefined}
        className={`w-full flex items-center gap-4 px-4 py-3 text-left transition-colors ${
          hasDetails ? 'hover:bg-dark-800/50 cursor-pointer' : 'cursor-default'
        }`}
      >
        <span
          className={`flex items-center justify-center w-7 h-7 rounded-full border text-sm font-semibold ${icon.cls}`}
        >
          {icon.symbol}
        </span>
        <span className={`flex-1 ${isFailed ? 'text-red-300' : 'text-dark-100'}`}>{step.label}</span>
        {duration && (
          <span className="text-xs text-dark-400 font-mono tabular-nums">{duration}</span>
        )}
        {hasDetails && (
          <span className="text-dark-500 text-xs">{isExpanded ? '▲' : '▼'}</span>
        )}
      </button>

      {hasDetails && isExpanded && (
        <div className="px-4 pb-4 pt-1 pl-15 ml-7 border-l border-dark-700">
          {isFailed && error && (
            <p className="text-red-300 text-sm py-2">{error.message}</p>
          )}
          {!isFailed && step.metrics && <StepMetrics metrics={step.metrics} />}
          {!isFailed && !step.metrics && (
            <p className="text-dark-400 text-sm py-2">In progress…</p>
          )}
        </div>
      )}
    </div>
  );
}

function PhaseEnCours({ metrics, t }) {
  if (!metrics.phase) return null;
  const p = metrics.phase_progress;
  const pourcent = p && p.total ? Math.min(100, Math.round((100 * p.fait) / p.total)) : null;

  return (
    <div className="py-2">
      <div className="flex items-baseline justify-between gap-4">
        <span className="text-dark-100 text-sm">
          {t(`trace.${metrics.phase}`, metrics.phase)}
        </span>
        {pourcent != null && (
          <span className="text-dark-400 text-xs font-mono tabular-nums">
            {p.fait} / {p.total}
          </span>
        )}
      </div>
      {pourcent != null && (
        <div className="mt-2 h-1.5 bg-dark-900 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary-500 rounded-full transition-all duration-500"
            style={{ width: `${pourcent}%` }}
          />
        </div>
      )}
    </div>
  );
}

/* La trace : ce que le calcul a franchi, dans l'ordre.
 *
 * Le serveur n'envoie que des CODES et des nombres — jamais le nom d'un outil
 * ni d'une méthode numérique. Les mots sont ici, dans les traductions, comme
 * pour les causes d'échec. C'est ce qui permet d'écrire « Calcul des
 * sensibilités » là où le solveur dit tout autre chose.
 */
function Trace({ evenements, t }) {
  if (!evenements || evenements.length === 0) return null;
  const recents = [...evenements].reverse();

  return (
    <div className="py-2">
      <p className="text-dark-500 text-xs uppercase tracking-wider mb-2">
        {t('trace.titre')}
      </p>
      <ol className="space-y-1">
        {recents.map((e, i) => (
          <li key={`${e.t}-${i}`} className="flex items-baseline gap-3 text-sm">
            <span className="text-dark-500 font-mono tabular-nums text-xs shrink-0">
              {e.t ? new Date(e.t).toLocaleTimeString(undefined, {
                hour: '2-digit', minute: '2-digit',
              }) : '—'}
            </span>
            <span className="text-dark-300">
              {e.code === 'iteration'
                ? t('trace.iteration', {
                    n: e.n,
                    objectif: typeof e.objectif === 'number' ? e.objectif.toFixed(6) : '—',
                  })
                : t(`trace.${e.code}`, e.code)}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function StepMetrics({ metrics }) {
  const { t } = useTranslation();
  const chiffres = [
    metrics.iteration != null && [t('timeline.iteration'), metrics.iteration, null],
    metrics.objective_current != null && [
      t('timeline.objectif'), metrics.objective_current.toFixed(6), null],
    metrics.objective_gain_percent != null && [
      t('timeline.gain'), `${metrics.objective_gain_percent > 0 ? '−' : '+'}${Math.abs(metrics.objective_gain_percent).toFixed(2)}`, '%'],
    metrics.cadence_min != null && [t('timeline.cadence'), metrics.cadence_min, 'min'],
    metrics.lift_current != null && [
      t('timeline.portance'), metrics.lift_current.toFixed(4), null],
  ].filter(Boolean);

  return (
    <div className="divide-y divide-dark-700">
      <PhaseEnCours metrics={metrics} t={t} />

      {chiffres.length > 0 && (
        <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 text-sm py-3">
          {chiffres.map(([libelle, valeur, unite]) => (
            <div key={libelle}>
              <dt className="text-dark-400 text-xs">{libelle}</dt>
              <dd className="text-dark-100 font-mono tabular-nums">
                {valeur}
                {unite && <span className="text-dark-500 text-xs ml-1">{unite}</span>}
              </dd>
            </div>
          ))}
        </dl>
      )}

      <Trace evenements={metrics.trace} t={t} />

      {metrics.last_update_at && (
        <p className="text-dark-500 text-xs py-2">
          {t('timeline.derniereNouvelle', { quand: relativeTime(metrics.last_update_at) })}
        </p>
      )}
    </div>
  );
}

export default function SimulationTimeline({ simulationId, onStatusChange }) {
  const { t } = useTranslation();
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await simulationService.getTimeline(simulationId);
      setTimeline(data);
      setError(null);
      // Bubble status changes up so the parent page can react (e.g. show results)
      if (onStatusChange) onStatusChange(data.overall_status);
    } catch (e) {
      setError(t('frise.indisponible'));
    } finally {
      setLoading(false);
    }
  }, [simulationId, onStatusChange, t]);

  useEffect(() => {
    load();
  }, [load]);

  // Auto-expand the current step
  useEffect(() => {
    if (timeline?.current_step_id && expanded === null) {
      setExpanded(timeline.current_step_id);
    }
    if (timeline?.error?.failed_step_id && expanded === null) {
      setExpanded(timeline.error.failed_step_id);
    }
  }, [timeline, expanded]);

  // Polling: only while the simulation is live
  useEffect(() => {
    if (!timeline) return undefined;
    if (timeline.overall_status === 'completed' || timeline.overall_status === 'failed') {
      return undefined;
    }
    const interval = setInterval(load, 10000);
    return () => clearInterval(interval);
  }, [timeline, load]);

  if (loading && !timeline) {
    return (
      <div className="bg-dark-800/50 border border-dark-700 rounded-xl p-6 text-dark-400 text-center">
        Loading progress…
      </div>
    );
  }

  if (error && !timeline) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-red-300 text-sm">
        {error}
      </div>
    );
  }

  if (!timeline) return null;

  const elapsed = formatDuration(timeline.elapsed_seconds);
  const headerStatusLabel = {
    queued: t('frise.enAttente'),
    running: 'In progress',
    completed: 'Completed',
    failed: 'Stopped',
  }[timeline.overall_status] || timeline.overall_status;

  const headerStatusCls = {
    queued: 'text-primary-300',
    running: 'text-primary-400',
    completed: 'text-green-400',
    failed: 'text-red-400',
  }[timeline.overall_status] || 'text-dark-300';

  return (
    <div className="bg-dark-800/50 border border-dark-700 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-dark-700">
        <div>
          <h3 className="text-dark-100 font-semibold">Progress</h3>
          <p className="text-xs text-dark-400 mt-0.5">
            <span className={headerStatusCls}>{headerStatusLabel}</span>
            {elapsed && (
              <>
                <span className="mx-2 text-dark-600">·</span>
                <span>{elapsed} elapsed</span>
              </>
            )}
          </p>
        </div>
      </div>
      <div>
        {(timeline.steps || []).map((step) => (
          <StepRow
            key={step.id}
            step={step}
            isCurrent={step.id === timeline.current_step_id}
            isExpanded={expanded === step.id}
            onToggle={() => setExpanded((prev) => (prev === step.id ? null : step.id))}
            error={timeline.error}
          />
        ))}
      </div>
    </div>
  );
}
